import {Component, EventEmitter, Input, Output} from "@angular/core";
import {TSelectionWorkout} from "../../../shared/interfaces/IWorkout";
import {MatDialog} from "@angular/material/dialog";
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData
} from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";
import {WorkoutsControllerService} from "../../../core/api/controllers/workouts/workouts-controller.service";
import {Router} from "@angular/router";
import {AuthService} from "../../../core/services/auth.service";
import {_pt} from "../../../core/services/preview-text/helpers/preview-text.function";

@Component({
  selector: 'app-workout-card',
  templateUrl: './workout-card.component.html',
  styleUrls: ['./workout-card.component.scss']
})
export class WorkoutCardComponent {
  @Input() workout: TSelectionWorkout;
  @Output() _delete = new EventEmitter<string>();
  @Input() isSelectionActive = false;
  isLoading = false;
  /** Set when the thumbnail URL fails to load, so the card falls back to artwork. */
  thumbnailFailed = false;

  constructor(
      private dialog: MatDialog,
      private workoutsController: WorkoutsControllerService,
      private router: Router,
      private authService: AuthService
  ) {}

  get playlistCount(): number {
    return this.workout?.playlists?.length ?? 0;
  }

  get exerciseCount(): number {
    return this.getExercisesCount();
  }

  getExercisesCount(): number {
    return (this.workout?.playlists ?? []).map(playlist => {
      return playlist.exercises?.length ?? 0;
    }).reduce((acc, curr) => {
      return acc + curr;
    }, 0);
  }

  get summary(): string {
    if (this.playlistCount === 0) {
      return _pt("this_workout_is_empty");
    }
    const playlists = `${this.playlistCount} ${this.playlistCount === 1 ? "playlist" : "playlists"}`;
    const exercises = `${this.exerciseCount} ${this.exerciseCount === 1 ? "exercise" : "exercises"}`;
    return `${playlists} · ${exercises}`;
  }

  /** Announces what activating the card does, which depends on whether a selection is in progress. */
  get cardActionLabel(): string {
    return this.isSelectionActive
      ? `${this.workout.isSelected ? "Deselect" : "Select"} ${this.workout.name}`
      : `Edit ${this.workout.name}`;
  }

  useFallbackImage(): void {
    this.thumbnailFailed = true;
  }

  toggleSelection(): void {
    this.workout.isSelected = !this.workout.isSelected;
  }

  openDeleteDialog(): void {
    const data: IConfirmationDialogData = {
      title: "Are you sure?",
      message: _pt('delete_workout_confirm_text')
    }
    this.dialog.open(ConfirmationDialogComponent, {
      data
    }).afterClosed().subscribe({
      next: (value) => {
        if(value) {
          this.deleteWorkout();
        }
      }
    })
  }

  deleteWorkout(): void {
    this.isLoading = true;
    this.workoutsController.toggleWorkoutStatus(this.workout.id, false).subscribe({
      next: () => {
        this._delete.emit(this.workout.id);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    })
  }

  onClick(): void {
    if(this.isLoading) {
      return;
    }
    if(this.isSelectionActive) {
      this.toggleSelection();
    } else {
      const workoutsLink = _pt("sidebar_workouts_link");
      this.router.navigateByUrl(`${workoutsLink}/edit/${this.workout.id}`);
    }
  }

  get isSuperadmin() {
    return this.authService.checkTokenAccess("SuperAdmin");
  }

  protected readonly _pt = _pt;
}
